import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { usePostDisable2FAMutation } from '@/features/auth/hooks/mutations/use-post-disable-2fa.ts';
import { useState } from 'react';
import { StatusAlert } from '@/features/auth/components/status-alert.tsx';
import { RectangleEllipsis } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { useAuthStore } from '@/services/store/use-auth-store.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';

export const TwoFactorDisableForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [disableStatus, setDisableStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const { isAuthenticated } = useAuthStore();

  const { email, disableCode } = {
    email: searchParams.get('email') || '',
    disableCode: searchParams.get('code') || '',
  };

  const { mutate: disableTwoFactor, isPending } = usePostDisable2FAMutation({
    onSuccess: () => {
      setDisableStatus({
        type: 'success',
        message: 'Two-factor authentication has been successfully disabled',
      });
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    },
    onError: () => {
      setDisableStatus({
        type: 'error',
        message: 'Failed to disable two-factor authentication. Please try again.',
      });
    },
  });

  async function handleDisableTwoFactor() {
    disableTwoFactor({ email, code: disableCode });
  }

  if (isAuthenticated || !disableCode || !email) {
    return (
      <div className="min-h-[390px]">
        <Navigate to="/" replace={true} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <div className="flex flex-col gap-4">
        {disableStatus && (
          <div className="flex w-full items-center justify-center">
            <StatusAlert
              type={disableStatus.type}
              message={disableStatus.message}
              className="max-w-[300px]"
            />
          </div>
        )}
        <div className="space-y-2 text-center">
          <div className="flex justify-center">
            <RectangleEllipsis size="52" className="text-[#383E42]" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
            Disable Two-Factor Authentication
          </h2>
          <p className="text-sm text-gray-600">
            Confirm your request to disable two-factor authentication
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            disabled={isPending}
            onClick={handleDisableTwoFactor}
            className="text-md h-10 flex-1 font-medium text-white shadow-sm transition-all hover:shadow-md"
            size="lg"
          >
            {isPending ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                <span className="text-[16px] select-none">Confirm Disable 2FA</span>
              </>
            ) : (
              <span className="text-[16px] select-none">Confirm Disable 2FA</span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorDisableForm;
